import { Component, Input, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PRODUCT_USE_MAPPINGS } from '@shared/db-fields-mappings/product-use.mappings';
import { SearchArea } from '@shared/enums/SearchArea';
import {
  TCenter,
  TCenterProjectsData,
  buildCenterMock,
} from '@shared/interfaces/TCenter';
import {
  TProductSalesTableData,
  convertToTableData,
} from '@shared/interfaces/TProductUse';
import {
  ICommonTableParams,
  PaginationVisibility,
  SortType,
  TCenterSalesTableRow,
  TTDataType,
  TTableRowType,
  TableRowType,
} from 'src/app/components/common-table/common-table.component.types';
import {
  TCenterParams,
  TComponentType,
} from 'src/app/interfaces/component-params.interface';
import { TDataColumnDef, buildColumnsDef } from 'src/utils/buildColumnsDef';
import { sortingDataAccessor } from 'src/utils/sortingDataAccessor';

@Component({
  selector: 'app-sales-table',
  templateUrl: './sales-table.component.html',
  styleUrls: ['./sales-table.component.scss'],
})
export class SalesTableComponent {
  @Input() public centerParams: TCenterParams = {
    __type: TComponentType.center,
    categoryId: '',
    centerId: '',
    projectId: '',
  };
  @Input() public centerData: TCenter = {
    ...buildCenterMock({}),
  };

  public columns: TDataColumnDef<TCenterSalesTableRow>[] = [
    ...buildColumnsDef({
      textColumns: [],
      fieldsMappings: Array.from(PRODUCT_USE_MAPPINGS.values()).filter(
        (f) => f.visibleInShortSummary
      ),
    }),
  ];

  public pageSize: number = 10;
  public pageSizeOptions: number[] = [10, 25, 50, 100, 1000];
  public pageIndex: number = 0;

  public params: ICommonTableParams<TTableRowType, TTDataType>;
  public dataSource: MatTableDataSource<TCenterSalesTableRow>;
  public salesCount: number = 0;
  public productSales: TProductSalesTableData[] = [];

  constructor() {
    this.dataSource = new MatTableDataSource();
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'time_period') {
        const timePeriod = (item as unknown as TProductSalesTableData).time_period;
        const month = parseInt(timePeriod.substring(1, 3), 10);
        const year = parseInt(timePeriod.substring(3), 10);
        return year * 100 + month;
      }
      return sortingDataAccessor(item, property);
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['centerData'] && this.isCenterSelected()) {
      const projectData = this.centerData.projectsData.find(
        (p) => p.projectId === this.centerParams.projectId
      );
      this._getProjectProductUseData(projectData);
      this.params = this.getTableParams();
    }
  }

  private getTableParams(): ICommonTableParams<TTableRowType, TTDataType> {
    return {
      columns: this.columns as TDataColumnDef<TTDataType>[],
      dataCount: this.salesCount,
      dataLoaded: true,
      dataSource: this.dataSource as MatTableDataSource<TTableRowType>,
      displayedColumns: Array.from(PRODUCT_USE_MAPPINGS.values())
        .filter((f) => f.visibleInShortSummary)
        .map((f) => f.fieldId),
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      paginationCallback: (e) => this.paginationCallback(e),
      downloadCallback: undefined,
      paginationVisibility: PaginationVisibility.Simple,
      importCallback: void 0,
      area: SearchArea.None,
      projectId: this.centerParams.projectId,
      selection: false,
      actionsAvailable: false,
      sortType: SortType.Simple,
      simpleSortHeaders: [`Product Name`, `Time Period`],
    };
  }

  public async paginationCallback(e: PageEvent): Promise<void> {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
  }

  private _getProjectProductUseData(
    projectData: TCenterProjectsData | undefined
  ): void {
    if (projectData === undefined || !projectData.products_use) {
      this.productSales = [];
      this.salesCount = 0;
      this.dataSource.data = [];
      return;
    }
    this.productSales = convertToTableData(projectData.products_use);
    this.salesCount = this.productSales.length;
    this.dataSource.data = this.productSales.map((sale, index) => {
      return {
        ...sale,
        position: index,
        __type: TableRowType.CenterSales,
      };
    });
  }

  private isCenterSelected(): boolean {
    return this.centerData.c_id !== '' && this.centerParams.centerId !== '';
  }
}
